import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Gift, Loader2, Check, X, RotateCcw, Download, ShieldAlert, DollarSign,
  Users, TrendingUp, Wallet, Save, Banknote, AlertTriangle, Copy, CalendarClock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  active: 'bg-emerald-500/15 text-emerald-600', pending: 'bg-amber-500/15 text-amber-600',
  approved: 'bg-blue-500/15 text-blue-600', withdrawn: 'bg-purple-500/15 text-purple-600',
  paid: 'bg-emerald-500/15 text-emerald-600', rejected: 'bg-red-500/15 text-red-600',
  reversed: 'bg-red-500/15 text-red-600', expired: 'bg-gray-500/15 text-gray-500',
  fraud_review: 'bg-orange-500/15 text-orange-600', under_review: 'bg-orange-500/15 text-orange-600',
};
const Status = ({ s }) => <Badge className={`${STATUS_COLORS[s] || 'bg-muted'} border-0 capitalize`}>{(s || '').replace('_', ' ')}</Badge>;

const Metric = ({ icon: Icon, label, value, testid }) => (
  <Card data-testid={testid}><CardContent className="p-4">
    <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1"><Icon className="w-4 h-4" />{label}</div>
    <p className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>{value}</p>
  </CardContent></Card>
);

export default function AdminReferralsPage() {
  const { token } = useAuth();
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [overview, setOverview] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [attributions, setAttributions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [flags, setFlags] = useState([]);
  const [config, setConfig] = useState(null);
  const [due, setDue] = useState(null);           // WO-71 — Payments to settle
  const [settle, setSettle] = useState(null);     // { row, mode: 'paid' | 'reject' }
  const [referrers, setReferrers] = useState([]); // WO-71 — tax residence + withholding
  const [loading, setLoading] = useState(true);

  const headers = useCallback(() => ({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const load = useCallback(async () => {
    try {
      // WO-36 — a refused request used to come back as {detail} and fill the
      // cards with blanks. Now any failure is reported instead.
      const get = (path) => fetch(`${API_URL}/api/referral/admin/${path}`, { headers: headers() })
        .then(async (r) => {
          const body = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(body.detail || `The server answered ${r.status}`);
          return body;
        });
      const [o, c, a, w, f, cfg, d, rf] = await Promise.all([
        get('overview'), get('commissions?page_size=100'), get('attributions?page_size=100'),
        get('withdrawals?page_size=100'), get('fraud-flags'), get('config'), get('payouts-due'), get('referrers'),
      ]);
      setOverview(o); setCommissions(c.items || []); setAttributions(a.items || []);
      setWithdrawals(w.items || []); setFlags(f.items || []); setConfig(cfg); setDue(d);
      setReferrers(rf.items || []);
    } catch (e) {
      toast.error(`Failed to load referral admin data: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);

  const post = async (path, body, okMsg) => {
    try {
      const res = await fetch(`${API_URL}/api/referral/admin/${path}`, {
        method: 'POST', headers: headers(), body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Action failed');
      toast.success(okMsg);
      load();
    } catch (e) { toast.error(e.message); }
  };

  // WO-36 — window.open cannot send the sign-in header, so both buttons
  // answered 401. Download through fetch instead.
  const exportCsv = async (type) => {
    try {
      const res = await fetch(`${API_URL}/api/referral/admin/export?type=${type}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`The server answered ${res.status}`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = `referral_${type}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-referrals-page">
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500"><Gift className="w-6 h-6 text-white" /></div>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>{isAr ? 'برنامج الإحالة' : 'Referral Program'}</h1>
          <p className="text-sm text-muted-foreground">{isAr ? 'إدارة العمولات والسحوبات والاحتيال' : 'Manage commissions, withdrawals & fraud'}</p>
        </div>
      </div>

      {/* WO-71 — Payments to settle: what is owed now, oldest first, and how late */}
      <PayoutsDue due={due} onSettle={(row, mode) => setSettle({ row, mode })}
        onSanctions={(user_id) => post('sanctions-checked', { user_id }, 'Sanctions check recorded')} />
      <SettleDialog settle={settle} onClose={() => setSettle(null)}
        onConfirm={async (body, mode) => {
          await post(mode === 'paid' ? 'approve-withdrawal' : 'reject-withdrawal', body,
            mode === 'paid' ? 'Marked as paid — the referrer has been e-mailed' : 'Request rejected — the referrer has been e-mailed');
          setSettle(null);
        }} />
      <ReferrersOwed items={referrers} token={token}
        onReview={(user_id, accepted, note) => post('certificate-review', { user_id, accepted, note },
          accepted ? 'Certificate accepted — the referrer has been e-mailed' : 'Certificate rejected — the referrer has been e-mailed')}
        onSanctions={(user_id) => post('sanctions-checked', { user_id }, 'Sanctions check recorded')} />

      {/* Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric icon={DollarSign} label={isAr ? 'إيرادات الإحالة' : 'Referral Revenue'} value={overview?.total_referral_revenue_display} testid="ov-revenue" />
        <Metric icon={Wallet} label={isAr ? 'عمولات مستحقة' : 'Commissions Owed'} value={overview?.commissions_owed_display} testid="ov-owed" />
        <Metric icon={Check} label={isAr ? 'عمولات مدفوعة' : 'Commissions Paid'} value={overview?.commissions_paid_display} testid="ov-paid" />
        <Metric icon={Users} label={isAr ? 'محيلون نشطون' : 'Active Referrers'} value={overview?.active_referrers} testid="ov-referrers" />
        <Metric icon={TrendingUp} label={isAr ? 'معدل التحويل' : 'Conversion Rate'} value={`${overview?.conversion_rate ?? 0}%`} testid="ov-conversion" />
        <Metric icon={DollarSign} label="ROI" value={overview?.roi != null ? `${overview.roi}x` : '—'} testid="ov-roi" />
        <Metric icon={Wallet} label={isAr ? 'سحوبات معلقة' : 'Pending Withdrawals'} value={overview?.pending_withdrawals} testid="ov-pendingw" />
        <Metric icon={ShieldAlert} label={isAr ? 'تنبيهات احتيال' : 'Open Fraud Flags'} value={overview?.open_fraud_flags} testid="ov-flags" />
      </div>

      <Tabs defaultValue="commissions">
        <TabsList className="max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="commissions" data-testid="atab-commissions">{isAr ? 'العمولات' : 'Commissions'}</TabsTrigger>
          <TabsTrigger value="attributions" data-testid="atab-attributions">{isAr ? 'الإسنادات' : 'Attributions'}</TabsTrigger>
          <TabsTrigger value="withdrawals" data-testid="atab-withdrawals">{isAr ? 'السحوبات' : 'Withdrawals'}</TabsTrigger>
          <TabsTrigger value="fraud" data-testid="atab-fraud">{isAr ? 'الاحتيال' : 'Fraud'}</TabsTrigger>
          <TabsTrigger value="settings" data-testid="atab-settings">{isAr ? 'الإعدادات' : 'Settings'}</TabsTrigger>
        </TabsList>

        {/* COMMISSIONS */}
        <TabsContent value="commissions">
          <div className="flex justify-end mb-3">
            <Button variant="outline" size="sm" onClick={() => exportCsv('commissions')} data-testid="export-commissions">
              <Download className="w-4 h-4 mr-2" />CSV</Button>
          </div>
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{isAr ? 'النوع' : 'Type'}</TableHead><TableHead>{isAr ? 'الدفعة' : 'Payment'}</TableHead>
                <TableHead>{isAr ? 'العمولة' : 'Commission'}</TableHead><TableHead>{isAr ? 'الحالة' : 'Status'}</TableHead>
                <TableHead>{isAr ? 'إجراءات' : 'Actions'}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {commissions.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No commissions</TableCell></TableRow> :
                commissions.map((c) => (
                  <TableRow key={c._id} data-testid={`acomm-row-${c._id}`}>
                    <TableCell className="capitalize text-sm">{(c.revenue_type || '').replace('_', ' ')}</TableCell>
                    <TableCell>{c.payment_display}</TableCell>
                    <TableCell className="font-semibold text-emerald-600">{c.commission_display}</TableCell>
                    <TableCell><Status s={c.status} /></TableCell>
                    <TableCell className="flex gap-1">
                      {(c.status === 'pending') && (
                        <Button size="sm" variant="outline" onClick={() => post('approve-commission', { commission_id: c._id }, 'Approved')} data-testid={`approve-comm-${c._id}`}>
                          <Check className="w-3.5 h-3.5" /></Button>)}
                      {(['pending', 'approved'].includes(c.status)) && (
                        <Button size="sm" variant="outline" onClick={() => post('reverse-commission', { commission_id: c._id, reason: 'Admin reversal' }, 'Reversed')} data-testid={`reverse-comm-${c._id}`}>
                          <RotateCcw className="w-3.5 h-3.5 text-red-500" /></Button>)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* ATTRIBUTIONS */}
        <TabsContent value="attributions">
          <div className="flex justify-end mb-3">
            <Button variant="outline" size="sm" onClick={() => exportCsv('attributions')} data-testid="export-attributions">
              <Download className="w-4 h-4 mr-2" />CSV</Button>
          </div>
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{isAr ? 'المحيل' : 'Referrer'}</TableHead><TableHead>{isAr ? 'العميل' : 'Referred'}</TableHead>
                <TableHead>{isAr ? 'الحالة' : 'Status'}</TableHead><TableHead>{isAr ? 'خصم' : 'Discount'}</TableHead>
                <TableHead>{isAr ? 'ينتهي' : 'Expires'}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {attributions.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No attributions</TableCell></TableRow> :
                attributions.map((a) => (
                  <TableRow key={a._id} data-testid={`attr-row-${a._id}`}>
                    <TableCell>{a.referrer_name || '—'}</TableCell>
                    <TableCell>{a.referred_company || '—'}</TableCell>
                    <TableCell><Status s={a.status} /></TableCell>
                    <TableCell>{a.discount_applied ? <Check className="w-4 h-4 text-emerald-500" /> : '—'}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{a.expires_at ? new Date(a.expires_at).toLocaleDateString() : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* WITHDRAWALS */}
        <TabsContent value="withdrawals">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{isAr ? 'المحيل' : 'Referrer'}</TableHead><TableHead>{isAr ? 'المبلغ' : 'Amount'}</TableHead>
                <TableHead>{isAr ? 'تفاصيل البنك' : 'Bank details'}</TableHead><TableHead>{isAr ? 'الحالة' : 'Status'}</TableHead>
                <TableHead>{isAr ? 'إجراءات' : 'Actions'}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {withdrawals.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No withdrawals</TableCell></TableRow> :
                withdrawals.map((w) => (
                  <TableRow key={w._id} data-testid={`awd-row-${w._id}`}>
                    <TableCell><div className="text-sm">{w.referrer_name}</div><div className="text-xs text-muted-foreground">{w.referrer_email}</div></TableCell>
                    <TableCell className="font-semibold">{w.amount_display}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[220px]">
                      {w.bank_details ? `${w.bank_details.bank_name} · ${w.bank_details.account_holder} · ${w.bank_details.account_number}` : '—'}
                    </TableCell>
                    <TableCell><Status s={w.status} /></TableCell>
                    <TableCell className="flex gap-1">
                      {w.status === 'pending' && (<>
                        <Button size="sm" variant="outline" onClick={() => post('approve-withdrawal', { withdrawal_id: w._id, notes: 'Paid' }, 'Withdrawal approved')} data-testid={`approve-wd-${w._id}`}>
                          <Check className="w-3.5 h-3.5 text-emerald-500" /></Button>
                        <Button size="sm" variant="outline" onClick={() => post('reject-withdrawal', { withdrawal_id: w._id, reason: 'Rejected by admin' }, 'Withdrawal rejected')} data-testid={`reject-wd-${w._id}`}>
                          <X className="w-3.5 h-3.5 text-red-500" /></Button>
                      </>)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* FRAUD */}
        <TabsContent value="fraud">
          <Card><CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{isAr ? 'النوع' : 'Type'}</TableHead><TableHead>{isAr ? 'التاريخ' : 'Date'}</TableHead>
                <TableHead>{isAr ? 'الحل' : 'Resolution'}</TableHead><TableHead>{isAr ? 'إجراءات' : 'Actions'}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {flags.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-10 text-muted-foreground">No fraud flags</TableCell></TableRow> :
                flags.map((f) => (
                  <TableRow key={f._id} data-testid={`flag-row-${f._id}`}>
                    <TableCell className="capitalize">{(f.flag_type || '').replace('_', ' ')}</TableCell>
                    <TableCell className="text-sm">{f.flagged_at ? new Date(f.flagged_at).toLocaleDateString() : '—'}</TableCell>
                    <TableCell>{f.resolution ? <Status s={f.resolution} /> : <Badge variant="outline">Open</Badge>}</TableCell>
                    <TableCell className="flex gap-1">
                      {!f.resolution && (<>
                        <Button size="sm" variant="outline" onClick={() => post('review-fraud-flag', { flag_id: f._id, resolution: 'approved', notes: 'Cleared' }, 'Flag cleared (approved)')} data-testid={`flag-approve-${f._id}`}>
                          <Check className="w-3.5 h-3.5 text-emerald-500" /></Button>
                        <Button size="sm" variant="outline" onClick={() => post('review-fraud-flag', { flag_id: f._id, resolution: 'rejected', notes: 'Fraud confirmed' }, 'Flag rejected (fraud)')} data-testid={`flag-reject-${f._id}`}>
                          <X className="w-3.5 h-3.5 text-red-500" /></Button>
                      </>)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent></Card>
        </TabsContent>

        {/* SETTINGS */}
        <TabsContent value="settings">
          <ConfigForm config={config} headers={headers} isAr={isAr} onSaved={load} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ConfigForm({ config, headers, isAr, onSaved }) {
  const [form, setForm] = useState({
    commission_rate: (config.commission_rate * 100).toString(),
    referral_duration_months: config.referral_duration_months.toString(),
    referred_discount_percent: (config.referred_discount_percent * 100).toString(),
    referred_discount_months: config.referred_discount_months.toString(),
    pending_days: config.pending_days.toString(),
    min_withdrawal_dollars: (config.min_withdrawal_cents / 100).toString(),
    payout_target_days: String(config.payout_target_days ?? 14),  // WO-71
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        commission_rate: parseFloat(form.commission_rate) / 100,
        referral_duration_months: parseInt(form.referral_duration_months, 10),
        referred_discount_percent: parseFloat(form.referred_discount_percent) / 100,
        referred_discount_months: parseInt(form.referred_discount_months, 10),
        pending_days: parseInt(form.pending_days, 10),
        min_withdrawal_cents: Math.round(parseFloat(form.min_withdrawal_dollars) * 100),
        payout_target_days: parseInt(form.payout_target_days, 10),
      };
      const res = await fetch(`${API_URL}/api/referral/admin/config`, {
        method: 'PUT', headers: headers(), body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Save failed');
      toast.success(isAr ? 'تم حفظ الإعدادات' : 'Settings saved');
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const fields = [
    ['commission_rate', isAr ? 'نسبة العمولة (٪)' : 'Commission rate (%)'],
    ['referral_duration_months', isAr ? 'مدة الإحالة (أشهر)' : 'Attribution window (months)'],
    ['referred_discount_percent', isAr ? 'خصم العميل (٪)' : 'Referred discount (%)'],
    ['referred_discount_months', isAr ? 'مدة الخصم (أشهر)' : 'Discount duration (months)'],
    ['pending_days', isAr ? 'فترة الانتظار (أيام)' : 'Pending period (days)'],
    ['min_withdrawal_dollars', isAr ? 'الحد الأدنى للسحب (دولار)' : 'Min withdrawal ($)'],
    ['payout_target_days', isAr ? 'مهلة الدفع بعد الطلب (أيام)' : 'Pay within (days of the request)'],
  ];

  return (
    <Card><CardContent className="p-6 space-y-4 max-w-2xl">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {fields.map(([k, label]) => (
          <div key={k} className="space-y-2">
            <Label>{label}</Label>
            <Input type="number" step="any" value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`cfg-${k}`} />
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={saving} data-testid="save-config-btn">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        {isAr ? 'حفظ الإعدادات' : 'Save settings'}
      </Button>
    </CardContent></Card>
  );
}

// WO-71 — the panel the admin watches so no referral payment is paid late.
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—');

const EU_LIST = 'https://www.sanctionsmap.eu/';   // official EU Sanctions Map (search by name)
const OFAC_LIST = 'https://sanctionssearch.ofac.treas.gov/';

function dueChip(r) {
  if (r.days_to_limit < 0) return { cls: 'bg-red-600/20 text-red-700 border-red-600/50', text: `${-r.days_to_limit} day(s) past the 30-day limit` };
  if (r.overdue) return { cls: 'bg-red-500/15 text-red-600 border-red-500/40', text: `${-r.days_to_due} day(s) late · ${r.days_to_limit} left before the 30-day limit` };
  if (r.days_to_due === 0) return { cls: 'bg-orange-500/15 text-orange-600 border-orange-500/40', text: 'Due today' };
  if (r.days_to_due <= 2) return { cls: 'bg-amber-500/15 text-amber-600 border-amber-500/40', text: `Due in ${r.days_to_due} day(s)` };
  return { cls: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30', text: `Due in ${r.days_to_due} days` };
}

function PayoutsDue({ due, onSettle, onSanctions }) {
  if (!due) return null;
  const items = due.items || [];
  const copy = (txt) => {
    try { navigator.clipboard.writeText(txt || ''); toast.success('Copied'); } catch (e) { /* ignore */ }
  };
  const border = due.overdue > 0 ? 'border-red-500/60' : items.length ? 'border-amber-500/50' : 'border-emerald-500/30';
  return (
    <Card className={`border-2 ${border}`} data-testid="payouts-due">
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Banknote className="w-5 h-5 text-emerald-600" />
            <h2 className="text-lg font-semibold">Payments to settle</h2>
            <span className="text-xs text-muted-foreground">target: paid within {due.target_days} days · Terms limit: {due.limit_days} days</span>
          </div>
          {due.overdue > 0 && (
            <Badge variant="outline" className="bg-red-500/15 text-red-600 border-red-500/40" data-testid="payouts-overdue">
              <AlertTriangle className="w-3.5 h-3.5 mr-1" />{due.overdue} late
            </Badge>
          )}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          <div><p className="text-muted-foreground">To transfer now</p><p className="text-xl font-bold" data-testid="payouts-total">{due.total_display} <span className="text-sm font-normal text-muted-foreground">({due.count})</span></p></div>
          <div><p className="text-muted-foreground">Oldest request</p><p className="text-xl font-bold">{items.length ? `${due.oldest_days} day(s)` : '—'}</p></div>
          <div><p className="text-muted-foreground">Owed, not yet requested</p><p className="text-xl font-bold">{due.available_not_requested_display} <span className="text-sm font-normal text-muted-foreground">({due.available_referrers})</span></p></div>
          <div><p className="text-muted-foreground">In the refund hold</p><p className="text-xl font-bold">{due.on_hold_display} <span className="text-sm font-normal text-muted-foreground">({due.on_hold_referrers})</span></p></div>
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="payouts-none">Nothing to pay right now.</p>
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Referrer</TableHead><TableHead>To transfer</TableHead><TableHead>Requested</TableHead>
              <TableHead>Pay by</TableHead><TableHead>Bank transfer details</TableHead><TableHead className="text-right">Action</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((r) => {
                const chip = dueChip(r);
                const b = r.bank_details || {};
                return (
                  <TableRow key={r._id} className={r.overdue ? 'bg-red-50/60 dark:bg-red-950/20' : ''} data-testid={`payout-row-${r._id}`}>
                    <TableCell><div className="text-sm font-medium">{r.referrer_name || '—'}</div><div className="text-xs text-muted-foreground">{r.referrer_email}</div>
                      <div className="text-xs text-muted-foreground">{r.acting_as === 'company' ? 'company' : 'private person'} · tax resident {r.tax_residence || '—'}</div></TableCell>
                    <TableCell className="font-semibold">{r.net_display}
                      {r.withholding_pct > 0 && (
                        <div className="text-xs font-normal text-muted-foreground">{r.amount_display} less {r.withholding_pct}% Spanish tax ({r.withheld_display})</div>
                      )}
                      {r.withholding_basis === 'certificate_pending' && <div className="text-xs font-normal text-amber-600">certificate to check — accepting it removes the deduction</div>}
                    </TableCell>
                    <TableCell className="text-sm">{fmtD(r.requested_at)}<div className="text-xs text-muted-foreground">{r.days_waiting} day(s) ago</div></TableCell>
                    <TableCell className="text-sm">{fmtD(r.due_at)}<div><Badge variant="outline" className={`mt-1 ${chip.cls}`}><CalendarClock className="w-3 h-3 mr-1" />{chip.text}</Badge></div></TableCell>
                    <TableCell className="text-xs space-y-0.5 max-w-[260px]">
                      <div className="text-muted-foreground">{r.method === 'international_bank' ? 'International transfer' : 'GCC transfer'}</div>
                      <div>{b.bank_name || '—'} · {b.account_holder || '—'}</div>
                      <div className="flex items-center gap-1 font-mono break-all">{b.account_number || '—'}
                        {b.account_number && <button type="button" onClick={() => copy(b.account_number)} title="Copy"><Copy className="w-3 h-3" /></button>}
                      </div>
                      {b.swift_bic && <div className="font-mono">SWIFT {b.swift_bic}</div>}
                      {r.name_mismatch && <div className="text-red-600">Account holder ≠ legal name ({b.legal_name}) — check before paying</div>}
                      {r.on_hold_spain && <div className="text-amber-700">Regular referrer resident in Spain — set up Spanish tax by hand first</div>}
                    </TableCell>
                    <TableCell className="text-right space-y-1 whitespace-nowrap">
                      {!r.sanctions_checked && (
                        <div className="text-xs text-left whitespace-normal max-w-[200px] ms-auto" data-testid={`payout-sanctions-${r._id}`}>
                          First payment: check the name on the <a className="underline" href={EU_LIST} target="_blank" rel="noreferrer">EU list</a> and <a className="underline" href={OFAC_LIST} target="_blank" rel="noreferrer">OFAC</a>, then
                          <Button size="sm" variant="outline" className="ms-1 mt-1" onClick={() => onSanctions(r.referrer_user_id)}>Sanctions checked</Button>
                        </div>
                      )}
                      <div className="space-x-1">
                        <Button size="sm" disabled={!r.sanctions_checked} onClick={() => onSettle(r, 'paid')} data-testid={`payout-paid-${r._id}`}>
                          <Check className="w-3.5 h-3.5 mr-1" />Mark as paid</Button>
                        <Button size="sm" variant="outline" onClick={() => onSettle(r, 'reject')} data-testid={`payout-reject-${r._id}`}>
                          <X className="w-3.5 h-3.5 text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function SettleDialog({ settle, onClose, onConfirm }) {
  const [paidOn, setPaidOn] = useState('');
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [nameChecked, setNameChecked] = useState(false);
  const [spainHandled, setSpainHandled] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (settle) { setPaidOn(new Date().toISOString().slice(0, 10)); setReference(''); setReason(''); setNameChecked(false); setSpainHandled(false); }
  }, [settle]);
  if (!settle) return null;
  const { row, mode } = settle;
  const blocked = mode === 'paid' && ((row.name_mismatch && !nameChecked) || (row.on_hold_spain && !spainHandled));
  const confirm = async () => {
    setBusy(true);
    try {
      if (mode === 'paid') {
        await onConfirm({ withdrawal_id: row._id, paid_on: paidOn, reference, notes: reference ? `Bank ref ${reference}` : 'Paid',
          name_checked: nameChecked, spain_handled: spainHandled }, 'paid');
      } else {
        await onConfirm({ withdrawal_id: row._id, reason: reason || 'Rejected by admin' }, 'reject');
      }
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!settle} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="settle-dialog">
        <DialogHeader>
          <DialogTitle>{mode === 'paid' ? `Mark ${row.net_display || row.amount_display} as paid` : `Reject ${row.amount_display}`}</DialogTitle>
          <DialogDescription>
            {mode === 'paid'
              ? `Only after the bank transfer to ${row.referrer_name || row.referrer_email} has been made. The app issues the payment document and e-mails them.`
              : 'The amount goes back to their available balance and they get an e-mail with your reason.'}
          </DialogDescription>
        </DialogHeader>
        {mode === 'paid' ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Transfer date</Label>
                <Input type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} data-testid="settle-date" /></div>
              <div className="space-y-2"><Label>Bank reference (optional)</Label>
                <Input value={reference} onChange={(e) => setReference(e.target.value)} data-testid="settle-ref" /></div>
            </div>
            {row.name_mismatch && (
              <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={nameChecked} onChange={(e) => setNameChecked(e.target.checked)} data-testid="settle-name-checked" />
                <span>I checked that the account belongs to the referrer (identity document if needed).</span></label>
            )}
            {row.on_hold_spain && (
              <label className="flex items-start gap-2 text-sm"><input type="checkbox" className="mt-1" checked={spainHandled} onChange={(e) => setSpainHandled(e.target.checked)} data-testid="settle-spain" />
                <span>Spanish income tax and VAT for this referrer have been set up by hand.</span></label>
            )}
          </div>
        ) : (
          <div className="space-y-2"><Label>Reason (they will read it)</Label>
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. the IBAN is incomplete — please send the request again" data-testid="settle-reason" /></div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={confirm} disabled={busy || blocked} variant={mode === 'paid' ? 'default' : 'destructive'} data-testid="settle-confirm">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{mode === 'paid' ? 'Mark as paid' : 'Reject request'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// WO-71 — everyone who has earned commission: regular-referrer status (Terms 8.3), tax
// country, tax-residence certificate (accept / reject) and the sanctions check.
function ReferrersOwed({ items, token, onReview, onSanctions }) {
  if (!items || items.length === 0) return null;
  const openCert = async (uid) => {
    try {
      const r = await fetch(`${API_URL}/api/referral/admin/certificate/${uid}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(String(r.status));
      window.open(URL.createObjectURL(await r.blob()), '_blank', 'noopener');
    } catch (e) { toast.error('The certificate could not be opened.'); }
  };
  return (
    <Card data-testid="referrers-owed">
      <CardContent className="p-5 space-y-3">
        <div>
          <h2 className="text-lg font-semibold">Referrers</h2>
          <p className="text-xs text-muted-foreground">
            Spanish tax is deducted automatically for regular referrers (Ambassadors, or more than 50 new paying customers in one calendar month): 24%, or 19% for EU/EEA residents, unless you accept their tax-residence certificate. Regular referrers resident in Spain are held for set-up by hand.
          </p>
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Referrer</TableHead><TableHead>Available</TableHead><TableHead>In hold</TableHead>
            <TableHead>Lifetime</TableHead><TableHead>Regular</TableHead><TableHead>Tax residence</TableHead>
            <TableHead>Certificate</TableHead><TableHead>Sanctions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((r) => (
              <TableRow key={r.user_id} data-testid={`referrer-row-${r.user_id}`}>
                <TableCell><div className="text-sm">{r.name || '—'}{r.is_ambassador && <Badge variant="outline" className="ml-2 text-fuchsia-600 border-fuchsia-500/40">Ambassador</Badge>}</div>
                  <div className="text-xs text-muted-foreground">{r.email} · Terms {r.terms_version || 'not accepted'}{r.participation !== 'active' ? ` · ${r.participation.replace('_', ' ')}` : ''}</div></TableCell>
                <TableCell>{r.available_display}</TableCell>
                <TableCell>{r.on_hold_display}</TableCell>
                <TableCell>{r.lifetime_display}</TableCell>
                <TableCell className="text-xs">{r.regular ? <span title={r.regular_reason}>yes</span> : 'no'}</TableCell>
                <TableCell>{r.tax_residence || <span className="text-muted-foreground text-xs">not given yet</span>}</TableCell>
                <TableCell className="text-xs space-y-1">
                  {r.certificate ? (<>
                    <button type="button" className="underline" onClick={() => openCert(r.user_id)}>{r.certificate.country} · {fmtD(r.certificate.issue_date)}</button>
                    <div>{r.certificate.status}</div>
                    {r.certificate.status === 'pending' && (
                      <div className="space-x-1">
                        <Button size="sm" variant="outline" onClick={() => onReview(r.user_id, true, '')} data-testid={`cert-accept-${r.user_id}`}><Check className="w-3.5 h-3.5" /></Button>
                        <Button size="sm" variant="outline" onClick={() => onReview(r.user_id, false, 'it does not show residence for the purposes of the tax treaty with Spain')} data-testid={`cert-reject-${r.user_id}`}><X className="w-3.5 h-3.5 text-red-500" /></Button>
                      </div>
                    )}
                  </>) : '—'}
                </TableCell>
                <TableCell className="text-xs">{r.sanctions_checked_at ? fmtD(r.sanctions_checked_at)
                  : <Button size="sm" variant="outline" onClick={() => onSanctions(r.user_id)}>Checked</Button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
